<template>
    <div style="margin-top: 50px" class="mb-15">
        <v-overlay :absolute="true" :value="submitting">
            <v-progress-circular indeterminate color="primary" class="mr-5"></v-progress-circular>
            <span>Submitting. Please wait...</span>
        </v-overlay>
        <div class="container-bg" style="margin-top: 5% !important; width: 700px; margin: auto">
            <div class="container-fg">
                <div style="display: flex; flex-direction: row">
                    <img style="width: 130px; margin-bottom: 1%" src="../../assets/logos/getac-logo.svg" />
                    <div class="text-h7 nav-font pl-3" style="margin-top: 1.3%">PAY QUOTE</div>
                </div>
                <v-divider style="margin-bottom: 10px; border-color: gray" />
                <div style="display: flex; flex-direction: row">
                    <div
                        v-if="!loading && quoteAccess"
                        class="quote-info pa-10"
                        style="display: block; margin: auto; width: 100%; background-color: rgb(56, 56, 56)"
                    >
                        <v-alert
                            v-if="quote.details.payment && quote.details.payment.status === 'COMPLETED'"
                            outlined
                            type="success"
                            text
                        >
                            PAID
                        </v-alert>
                        <v-alert v-else-if="quote.status === 'Cancelled'" outlined type="error" text>
                            CANCELED
                        </v-alert>
                        <v-row>
                            <v-col cols="6" class="text-start">
                                <v-list-item-subtitle>{{ quote.shipping.contact.companyName }} </v-list-item-subtitle>
                                <v-list-item-subtitle>{{
                                    quote.shipping.contact.firstName + ' ' + quote.shipping.contact.lastName
                                }}</v-list-item-subtitle>
                                <v-list-item-subtitle>{{ quote.shipping.contact.emailAddress }}</v-list-item-subtitle>
                                <v-list-item-subtitle>{{ quote.shipping.contact.phoneNumber }}</v-list-item-subtitle>
                            </v-col>
                            <v-col cols="6" class="text-right">
                                <v-list-item-subtitle>{{ quote.shipping.address.line1 }}</v-list-item-subtitle>
                                <v-list-item-subtitle v-if="quote.shipping.address.line2">{{
                                    quote.shipping.address.line2
                                }}</v-list-item-subtitle>
                                <v-list-item-subtitle>{{
                                    quote.shipping.address.city + ', ' + quote.shipping.address.state
                                }}</v-list-item-subtitle>
                                <v-list-item-subtitle>{{
                                    quote.shipping.address.country + ', ' + quote.shipping.address.postalCode
                                }}</v-list-item-subtitle>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="4">
                                <base-info-card title="Quote Number" color="primary">
                                    <v-list-item-subtitle> {{ quote.uid }}</v-list-item-subtitle>
                                </base-info-card>
                            </v-col>
                            <v-col cols="4" class="text-right">
                                <base-info-card title="Serial Number" color="primary">
                                    <v-list-item-subtitle> {{ quote.reference }}</v-list-item-subtitle>
                                </base-info-card>
                            </v-col>
                            <v-col cols="4" class="text-right">
                                <base-info-card title="Service Type" color="primary">
                                    <v-list-item-subtitle> {{ quote.service }}</v-list-item-subtitle>
                                </base-info-card>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="4" class="text-right">
                                <base-info-card title="Quote Items" color="primary"> </base-info-card>
                            </v-col>
                            <v-simple-table dark style="width: 100%; background-color: transparent">
                                <template v-slot>
                                    <thead>
                                        <tr>
                                            <th class="text-left"> Name </th>
                                            <th class="text-left"> Description </th>
                                            <th class="text-left"> Quantity </th>
                                            <th class="text-left"> Total </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(item, index) in quote.items" :key="index">
                                            <td>{{ item.uid }}</td>
                                            <td>{{ item.name }}</td>
                                            <td>{{ item.quantity }}</td>
                                            <td>{{ item.total }}</td>
                                        </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </v-row>
                        <br />
                        <base-info-card title="Quote Remarks" color="primary">
                            <v-textarea outlined readonly full-width height="300px" :value="quote.quoteRemark" dark>
                                {{ quote.quoteRemark }}</v-textarea
                            >
                        </base-info-card>
                        <br />
                        <v-row class="mt-5">
                            <v-col class="text-right">
                                <base-info-card title="Subtotal" color="primary">
                                    <v-list-item-subtitle> ${{ quote.subtotal.toFixed(2) }}</v-list-item-subtitle>
                                </base-info-card>
                                <base-info-card title="Freight" color="primary">
                                    <v-list-item-subtitle> ${{ quote.amount.freight }}</v-list-item-subtitle>
                                </base-info-card>
                                <base-info-card title="Total Amount Due" color="primary">
                                    <v-list-item-subtitle> ${{ quote.total }}</v-list-item-subtitle>
                                </base-info-card>
                            </v-col>
                        </v-row>
                        <v-row v-if="validate('PO')" class="mt-5">
                            <v-col class="text-left" cols="12">
                                <v-form
                                    id="auth_payment_form"
                                    ref="auth-payment-form"
                                    :action="authTransactionUrl"
                                    method="POST"
                                >
                                    <base-info-card title="Payment Method" color="primary">
                                        <input type="hidden" name="x_login" :value="authLoginId" />
                                        <input type="hidden" name="x_amount" :value="quote.total" />
                                        <input type="hidden" name="x_description" value="Service Quote" />
                                        <input type="hidden" name="x_invoice_num" :value="quote.uid" />
                                        <input type="hidden" name="x_line_item" :value="lineItems" />
                                        <input type="hidden" name="x_freight" :value="quote.amount.freight" />
                                        <input type="hidden" name="x_fp_sequence" :value="quote.uid" />
                                        <input type="hidden" name="x_fp_timestamp" :value="timestamp" />
                                        <input type="hidden" name="x_fp_hash" :value="fingerPrintHash" />
                                        <input type="hidden" name="x_show_form" value="PAYMENT_FORM" />
                                        <input type="hidden" name="x_relay_response" value="TRUE" />
                                        <input type="hidden" name="x_relay_url" :value="authRelayUrl" />
                                        <v-list-item-subtitle
                                            ><input type="radio" name="x_method" value="CC" checked="checked" /> Credit
                                            Card</v-list-item-subtitle
                                        >
                                        <v-list-item-subtitle
                                            ><input type="radio" name="x_method" value="ECHECK" />
                                            e-Check</v-list-item-subtitle
                                        >
                                        <v-btn
                                            color="primary"
                                            outlined
                                            class="text-center"
                                            style="display: block; margin: auto; margin-top: 20px"
                                            type="submit"
                                        >
                                            Pay Now
                                        </v-btn>
                                    </base-info-card>
                                </v-form>
                            </v-col>
                        </v-row>
                        <v-row v-if="validate('PayPal')" class="mt-5">
                            <span>
                                <v-list-item>
                                    <v-list-item-content
                                        :data-cy="`info-card-key-value-payment-method`"
                                        class="align-end text-body-2 field-values"
                                    >
                                        <base-info-card :title="poTitle" style="color: white" color="primary">
                                            <v-text-field
                                                v-model="poNumber"
                                                outlined
                                                dark
                                                :label="poLabel"
                                                dense
                                            ></v-text-field>
                                        </base-info-card>
                                        <base-info-card
                                            v-if="isMcElroy"
                                            style="color: white"
                                            :title="releaseTitle"
                                            color="primary"
                                        >
                                            <v-text-field
                                                v-model="releaseNumber"
                                                outlined
                                                dark
                                                :label="releaseLabel"
                                                dense
                                            ></v-text-field>
                                        </base-info-card>
                                    </v-list-item-content>
                                </v-list-item>
                            </span>
                            <span>
                                <v-list-item style="margin-top: -35px">
                                    <v-list-item-content
                                        :data-cy="`info-card-key-value-payment-method`"
                                        class="align-end text-body-2 field-values"
                                    >
                                        <po-uploader :dark-mode="true" @fileId="getFileName($event)"></po-uploader>
                                    </v-list-item-content>
                                </v-list-item>
                            </span>
                            <v-row class="mt-5">
                                <v-col class="text-center">
                                    <v-btn
                                        color="primary"
                                        outlined
                                        class="text-center"
                                        :disabled="unableToSumbitPO"
                                        style="margin-bottom: 25%; margin-left: 10px"
                                        @click="proceedPO"
                                        >Submit PO</v-btn
                                    >
                                </v-col>
                            </v-row>
                        </v-row>
                    </div>
                </div>
            </div>
        </div>
        <v-snackbar v-model="snackbar" color="error">
            {{ snackbarMessage }}

            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" color="white" @click="snackbar = false"> Close </v-btn>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
import gql from 'graphql-tag'
import PoUploader from '@/components/po_uploader/po_uploader.vue'

export default {
    name: 'PayQuoteByAuthorize',
    components: { PoUploader },
    props: {
        quoteid: {
            type: String,
            required: false,
            default: ``
        },
        reference: {
            type: String,
            required: false,
            default: ``
        }
    },
    data() {
        return {
            quoteReady: false,
            quote: null,

            authLoginId: process.env.VUE_APP_AUTHORIZE_NET_CLIENT_ID,
            authTransactionUrl: process.env.VUE_APP_AUTHORIZE_NET_URL,
            timestamp: '',

            submitting: false,
            quoteAccess: false,
            poUpload: '',
            poNumber: '',
            poSubmitted: false,
            loading: true,
            snackbar: false,
            snackbarMessage: '',
            poTitle: 'Enter PO Number',
            poLabel: 'PO #',
            releaseTitle: 'Enter Release Number',
            releaseLabel: 'Release # (Ex: 0116)',
            releaseNumber: '',
            fingerPrintHash: '',
            authRelayUrl: `${process.env.VUE_APP_AUTHORIZE_NET_RELAY_API}/payments/authpay/authresponse` 
        }
    },
    computed: {
        isMcElroy() {
            return (this.$auth?.user?.email || '').endsWith('@mcelroy.com')
        },
        unableToSumbitPO() {
            const regex = /^\d{4,5}$/
            return (
                !this.poNumber ||
                !this.poUpload ||
                this.poSubmitted ||
                (this.isMcElroy && !regex.test(this.releaseNumber))
            )
        },
        lineItems() {
            return `Service<|>Service<|><|>1<|>${this.quote.subtotal.toFixed(2)}<|>Y`
        }
    },
    watch: {
        quote: {
            immediate: true,
            handler() {
                if (!this.quote?.uid) return
                this.loading = true
                this.timestamp = Math.floor(Date.now() / 1000) + ''
                this.$apollo
                    .query({
                        query: gql`
                            query Query($timestamp: String!, $sequence: String!, $amount: Float!) {
                                generateFingerPrint(timestamp: $timestamp, sequence: $sequence, amount: $amount)
                            }
                        `,
                        variables: { timestamp: this.timestamp, sequence: this.quote.uid, amount: this.quote.total }
                    })
                    .then((data) => {
                        this.fingerPrintHash = data.data.generateFingerPrint
                    })
                    .finally(() => {
                        this.loading = false
                    })
            }
        },
        quoteid: {
            immediate: true,
            handler() {
                this.loading = true
                this.$apollo
                    .query({
                        query: gql`
                            query GetSingleQuote($uid: String!) {
                                getSingleQuote(uid: $uid) {
                                    uid
                                    customerSaved
                                    service
                                    reference
                                    externalResource
                                    status
                                    paymentMethod
                                    shipping {
                                        address {
                                            line1
                                            line2
                                            city
                                            state
                                            country
                                            postalCode
                                            international
                                            apo
                                        }
                                        contact {
                                            emailAddress
                                            phoneNumber
                                            firstName
                                            lastName
                                            companyName
                                            fullName
                                        }
                                    }
                                    billing {
                                        address {
                                            line1
                                            line2
                                            city
                                            state
                                            country
                                            postalCode
                                            international
                                            apo
                                        }
                                        contact {
                                            emailAddress
                                            phoneNumber
                                            firstName
                                            lastName
                                            companyName
                                            fullName
                                        }
                                    }
                                    items {
                                        uid
                                        name
                                        price
                                        quantity
                                        tax
                                        total
                                    }
                                    amount {
                                        freight
                                        handling
                                        discount
                                        tax
                                    }
                                    redirects {
                                        completed
                                        canceled
                                    }
                                    details {
                                        payment {
                                            uid
                                            source
                                            url
                                            status
                                            transaction
                                            adminStatus
                                            possibleStatusList
                                        }
                                        tax {
                                            uid
                                            source
                                            status
                                        }
                                    }
                                    createdAt
                                    updatedAt
                                    quoteRemark
                                    customerPo {
                                        poNumber
                                        poFileId
                                    }
                                    createdBy
                                    subtotal
                                    total
                                }
                            }
                        `,
                        variables: {
                            uid: this.quoteid
                        }
                    })
                    .then((data) => {
                        this.quote = data.data.getSingleQuote
                        if (this.quote.reference === this.reference) {
                            this.quoteAccess = true
                        }
                    })
                    .finally(() => {
                        this.loading = false
                        this.quoteReady = true
                    })
            }
        }
    },
    methods: {
        validate(wrongType) {
            let allowed = false
            if (this.quote.details.payment) {
                allowed = this.quote.details.payment.status !== 'COMPLETED' && this.quote.status !== 'Cancelled'
            } else if (this.quote.details.payment === null) {
                allowed = wrongType !== 'PO' ? this.quote.customerPo.poFileId === null : true
            }
            return this.quote && this.quote.paymentMethod !== wrongType && allowed
        },
        getFileName(file) {
            this.poUpload = file.file
        },
        proceedPO() {
            this.submitting = true
            this.poSubmitted = true
            const po = this.isMcElroy ? `${this.poNumber}-${this.releaseNumber}` : this.poNumber
            this.$apollo
                .mutate({
                    mutation: gql`
                        mutation AuthorizePOQuote(
                            $approved: Boolean!
                            $quoteId: String!
                            $poNumber: String
                            $poFileId: String
                        ) {
                            authorizePOQuote(
                                approved: $approved
                                quoteId: $quoteId
                                poNumber: $poNumber
                                poFileId: $poFileId
                            )
                        }
                    `,
                    variables: {
                        quoteId: this.quoteid,
                        poNumber: po,
                        poFileId: this.poUpload,
                        approved: true
                    }
                })
                .then((data) => {
                    if (data.data.authorizePOQuote === 'ok') {
                        this.$router.push({ name: 'service-quote-success' })
                    } else {
                        this.poSubmitted = false
                    }
                })
        }
    }
}
</script>
